import {
  SEAT_MAP_CLOCKWISE_ROTATION_DEG,
  SEAT_MAP_CLOCKWISE_ROTATION_SHOW_IDS,
  SEAT_MAP_MAX_SCALE,
} from '../constants/seatMap';
import type { ResolveSeatMapViewportParams, ResolvedSeatMapViewport } from '../models/types';
import {
  buildCameraFitScale,
  buildCenteredViewportPosition,
  clampViewportPosition,
} from './camera';
import {
  buildCameraFitScale as buildLegacyCameraFitScale,
  clampViewportDragPosition as clampLegacyViewportDragPosition,
  clampViewportPosition as clampLegacyViewportPosition,
} from './cameraLegacy';
import { rotateBounds } from './geometry';

const finiteOr = (value: unknown, fallback: number) =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

const normalizeAngle = (angleDeg: number) => ((((angleDeg + 180) % 360) + 360) % 360) - 180;

export const isSeatMapClockwiseRotationShow = (showId?: number) =>
  showId !== undefined && SEAT_MAP_CLOCKWISE_ROTATION_SHOW_IDS.includes(showId);

export const resolveLegacySeatMapViewport = ({
  rules,
  isMobile,
  sourceBounds,
  rotationPivot,
  screenWidth,
  screenHeight,
}: ResolveSeatMapViewportParams): ResolvedSeatMapViewport => {
  const rotationDeg = isMobile ? rules.rotation.mobileDeg : rules.rotation.desktopDeg;
  const configuredInitialScale = isMobile ? rules.initialScale.mobile : rules.initialScale.desktop;
  const configuredMinScale = isMobile ? rules.minScale.mobile : rules.minScale.desktop;
  const contentWidth = Math.max(1, sourceBounds.maxX - sourceBounds.minX);
  const contentHeight = Math.max(1, sourceBounds.maxY - sourceBounds.minY);
  const fitPadding = 48;
  const fitScaleX = screenWidth / (contentWidth + fitPadding * 2);
  const fitScaleY = screenHeight / (contentHeight + fitPadding * 2);
  const fallbackFitScale = Math.max(
    configuredMinScale,
    Math.min(2, Math.min(fitScaleX, fitScaleY))
  );
  const rotatedBounds = rotateBounds(sourceBounds, rotationPivot, rotationDeg);
  const fitScale = buildLegacyCameraFitScale(rotatedBounds, screenWidth, screenHeight, isMobile);
  const scale = Math.min(
    2,
    Math.max(configuredMinScale, Math.min(configuredInitialScale || fallbackFitScale, fitScale))
  );
  const boundsCenter = {
    x: (rotatedBounds.minX + rotatedBounds.maxX) / 2,
    y: (rotatedBounds.minY + rotatedBounds.maxY) / 2,
  };
  const centeredPosition = buildCenteredViewportPosition(
    boundsCenter,
    scale,
    screenWidth,
    screenHeight
  );
  const position = isMobile
    ? clampLegacyViewportPosition({
        position: centeredPosition,
        scale,
        bounds: rotatedBounds,
        screenWidth,
        screenHeight,
        isMobile: true,
      })
    : clampLegacyViewportDragPosition({
        position: centeredPosition,
        scale,
        bounds: rotatedBounds,
        screenWidth,
        screenHeight,
      });

  return {
    rotationDeg,
    scale,
    minimumScale: configuredMinScale,
    position,
    cameraBounds: { bounds: rotatedBounds, screenWidth, screenHeight },
    diagnostics: {
      device: isMobile ? 'mobile' : 'desktop',
      rotationSource: isMobile ? 'mobileDeg' : 'desktopDeg',
      configuredRotationDeg: rotationDeg,
      providedMobileRotationDeg: rules.rotation.mobileDeg,
      rotationAdjustmentDeg: 0,
      appliedRotationDeg: rotationDeg,
      configuredInitialScale,
      configuredMinScale,
      fitScale,
      appliedScale: scale,
      sourceBounds,
      rotatedBounds,
      viewport: { width: screenWidth, height: screenHeight },
    },
  };
};

export const resolveSeatMapViewport = ({
  rules,
  showId,
  isMobile,
  sourceBounds,
  rotationPivot,
  screenWidth,
  screenHeight,
}: ResolveSeatMapViewportParams): ResolvedSeatMapViewport => {
  const desktopRotation = finiteOr(rules.rotation?.desktopDeg, 0);
  const providedMobileRotation = Number.isFinite(rules.rotation?.mobileDeg)
    ? rules.rotation.mobileDeg
    : null;
  const configuredRotation = isMobile
    ? finiteOr(providedMobileRotation, desktopRotation)
    : desktopRotation;
  const rotationAdjustmentDeg = isSeatMapClockwiseRotationShow(showId)
    ? SEAT_MAP_CLOCKWISE_ROTATION_DEG
    : 0;
  const rotationDeg = normalizeAngle(configuredRotation + rotationAdjustmentDeg);
  const configuredInitialScale = finiteOr(
    isMobile ? rules.initialScale?.mobile : rules.initialScale?.desktop,
    0
  );
  const configuredMinScale = Math.max(
    0,
    finiteOr(isMobile ? rules.minScale?.mobile : rules.minScale?.desktop, 0)
  );
  const rotatedBounds = rotateBounds(sourceBounds, rotationPivot, rotationDeg);
  const fitScale = buildCameraFitScale(rotatedBounds, screenWidth, screenHeight, isMobile);
  const minimumScale = Math.min(configuredMinScale, fitScale);
  const scale = Math.min(SEAT_MAP_MAX_SCALE, Math.max(minimumScale, fitScale));
  const boundsCenter = {
    x: (rotatedBounds.minX + rotatedBounds.maxX) / 2,
    y: (rotatedBounds.minY + rotatedBounds.maxY) / 2,
  };
  const centeredPosition = buildCenteredViewportPosition(
    boundsCenter,
    scale,
    screenWidth,
    screenHeight
  );
  const position = clampViewportPosition({
    position: centeredPosition,
    scale,
    bounds: rotatedBounds,
    screenWidth,
    screenHeight,
    isMobile,
  });

  return {
    rotationDeg,
    scale,
    minimumScale,
    position,
    cameraBounds: {
      bounds: rotatedBounds,
      screenWidth,
      screenHeight,
    },
    diagnostics: {
      device: isMobile ? 'mobile' : 'desktop',
      rotationSource: isMobile && providedMobileRotation !== null ? 'mobileDeg' : 'desktopDeg',
      configuredRotationDeg: configuredRotation,
      providedMobileRotationDeg: providedMobileRotation,
      rotationAdjustmentDeg,
      appliedRotationDeg: rotationDeg,
      configuredInitialScale,
      configuredMinScale,
      fitScale,
      appliedScale: scale,
      sourceBounds,
      rotatedBounds,
      viewport: { width: screenWidth, height: screenHeight },
    },
  };
};
